import {useState} from "react";
import {useForm, Controller} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {App as AntdApp, Alert, Tag, Tooltip} from "antd";
import className from "classnames/bind";
import {Button, FontAwesomeIcon} from "~/components";
import {InputField} from "~/components/Forms";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {useAddKeywordItemsMutation, useGenerateKeywordIdeasMutation} from "~/reduxs/api/keywordApiSlice";
import {volumeText} from "./keywordMetrics";
import style from "../style/Keyword.module.scss";

const cn = className.bind(style);

const schema = Yup.object().shape({
	seed: Yup.string().trim().required('Vui lòng nhập từ khoá gốc / chủ đề'),
});

/** Màu tag theo ý định tìm kiếm AI gán cho cụm. */
const INTENT_COLORS = {
	'thông tin': 'blue',
	'thương mại': 'purple',
	'giao dịch': 'green',
	'điều hướng': 'default',
};

/**
 * Tab "Ý tưởng AI": nhập từ khoá gốc → AI xây các CỤM ý tưởng (task `keyword_ideas`,
 * tốn token AI theo gói / khoá riêng nếu có) → BE đối chiếu ngược GSC: từ nào site đã
 * hiển thị sẽ có badge impressions/vị trí THẬT. Lưu từng từ / cả cụm vào Kho từ khoá
 * (nguồn `ai`, kèm intent). Lịch sử sinh nằm trong tab Lịch sử AI của dự án.
 */
function KeywordIdeas({projectId}) {

	const {notification} = AntdApp.useApp();
	const [generate, {isLoading: generating}] = useGenerateKeywordIdeasMutation();
	const [addItems, {isLoading: saving}] = useAddKeywordItemsMutation();

	const [result, setResult] = useState(null);
	// Từ khoá đã lưu SAU khi có kết quả (server chỉ trả cờ saved tại thời điểm sinh).
	const [savedLocal, setSavedLocal] = useState(() => new Set());
	const [savingKey, setSavingKey] = useState(null);

	const {control, handleSubmit, formState: {errors}} = useForm({
		defaultValues: {seed: '', context: ''},
		resolver: yupResolver(schema),
	});

	const onGenerate = async ({seed, context}) => {
		try {
			const data = await generate({projectId, seed: seed.trim(), context: context?.trim() || ''}).unwrap();
			setResult(data);
			setSavedLocal(new Set());
			if (!data?.clusters?.length) {
				notification.warning({message: 'Không có kết quả', description: 'AI không trả về cụm từ khoá nào — thử lại hoặc đổi từ khoá gốc.'});
			}
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không sinh được ý tưởng từ khoá')});
		}
	};

	const isSaved = (kw) => kw.saved || savedLocal.has(kw.keyword);

	const save = async (cluster, keywords, key) => {
		const list = keywords.filter((kw) => !isSaved(kw));
		if (!list.length) return;
		try {
			setSavingKey(key);
			const res = await addItems({
				projectId,
				items: list.map((kw) => ({
					keyword: kw.keyword,
					source: 'ai',
					...(cluster.intent ? {intent: cluster.intent} : {}),
					...(kw.gsc ? {impressions: kw.gsc.impressions, position: kw.gsc.position} : {}),
					meta: {cluster: cluster.name, seed: result?.seed || ''},
				})),
			}).unwrap();
			setSavedLocal((prev) => {
				const next = new Set(prev);
				list.forEach((kw) => next.add(kw.keyword));
				return next;
			});
			notification.success({message: 'Đã lưu vào kho', description: `${res?.added ?? list.length} từ khoá mới${res?.skipped ? ` (${res.skipped} đã có sẵn)` : ''}.`});
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không lưu được từ khoá')});
		} finally {
			setSavingKey(null);
		}
	};

	const usage = result?.usage;

	return (
		<div>
			<Alert type="info" showIcon style={{marginBottom: 14}}
				message="AI chỉ sinh Ý TƯỞNG (không bịa volume). Từ khoá nào site đã từng hiển thị trên Google sẽ có badge impressions/vị trí thật từ Search Console." />

			{/* Scope class toàn cục `form` để InputField (.form-group/.form-control) ăn style chuẩn của site. */}
			<div className={cn('section') + ' form'}>
				<h3 className={cn('sectionTitle')}>Sinh cụm ý tưởng từ khoá</h3>
				<Controller control={control} name="seed" render={({field}) => (
					<InputField label="Từ khoá gốc / chủ đề" placeholder="vd: dịch vụ seo" errors={errors} {...field} />
				)} />
				<Controller control={control} name="context" render={({field}) => (
					<InputField label="Ngành / ngữ cảnh / khu vực (tuỳ chọn)" placeholder="vd: agency tại TP.HCM, khách doanh nghiệp nhỏ" errors={errors} {...field} />
				)} />
				<Button primary loading={generating} onClick={handleSubmit(onGenerate)}
					leftIcon={<FontAwesomeIcon icon="fa-light fa-wand-magic-sparkles" />}>
					Sinh ý tưởng
				</Button>
				{usage && (
					<span className={cn('hint')} style={{marginLeft: 12}}>
						Đã dùng {(usage.input + usage.output).toLocaleString('vi-VN')} token
						{result?.used_own_key ? ' (khoá AI riêng — không trừ hạn mức gói)' : ''}
						{result?.provider ? ` · ${result.provider}` : ''}
					</span>
				)}
			</div>

			{result?.clusters?.map((cluster, i) => (
				<div className={cn('section')} key={i}>
					<div className={cn('actionBar')} style={{marginBottom: 10}}>
						<h3 className={cn('sectionTitle')} style={{margin: 0}}>
							{cluster.name || `Cụm ${i + 1}`}
							{cluster.intent && (
								<Tag color={INTENT_COLORS[cluster.intent] || 'default'} style={{marginLeft: 10}}>{cluster.intent}</Tag>
							)}
						</h3>
						<Button small white disabled={saving} loading={savingKey === `cluster-${i}`}
							onClick={() => save(cluster, cluster.keywords, `cluster-${i}`)}
							leftIcon={<FontAwesomeIcon icon="fa-light fa-bookmark" />}>
							Lưu cả cụm
						</Button>
					</div>

					<div className={cn('ideaList')}>
						{cluster.keywords.map((kw) => (
							<div className={cn('ideaRow')} key={kw.keyword}>
								<span className={cn('ideaKeyword')}>
									{kw.keyword}
									{isSaved(kw) && <FontAwesomeIcon icon="fa-solid fa-circle-check" style={{color: '#16a34a', marginLeft: 6}} />}
								</span>
								<span className={cn('ideaMeta')}>
									{kw.metrics && kw.metrics.volume !== null && (
										<Tooltip title={`Volume tháng (theo Google Ads)${kw.metrics.kd !== null ? ` · độ khó SEO ${kw.metrics.kd}/100` : ''}`}>
											<Tag color="geekblue">
												{volumeText(kw.metrics.volume)} vol{kw.metrics.kd !== null ? ` · KD ${kw.metrics.kd}` : ''}
											</Tag>
										</Tooltip>
									)}
									{kw.gsc ? (
										<Tooltip title={`${kw.gsc.clicks} clicks · CTR ${(kw.gsc.ctr * 100).toFixed(1)}% (${kw.gsc.from} → ${kw.gsc.to})`}>
											<Tag color="green">
												{kw.gsc.impressions.toLocaleString('vi-VN')} impr · vị trí {Number(kw.gsc.position).toFixed(1)}
											</Tag>
										</Tooltip>
									) : (
										<Tag>chưa có dữ liệu GSC</Tag>
									)}
									<Button small white disabled={isSaved(kw) || saving} loading={savingKey === `kw-${kw.keyword}`}
										onClick={() => save(cluster, [kw], `kw-${kw.keyword}`)}
										leftIcon={<FontAwesomeIcon icon="fa-light fa-bookmark" />} />
								</span>
							</div>
						))}
					</div>
				</div>
			))}
		</div>
	);
}

export default KeywordIdeas;
